import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";
import {
  getOrders,
  getOrderStats,
  updateOrderStatus,
} from "../../services/orders-api";

import "../../style/orders.css";

import PendingOrdersSvg from "../../assets/pending-orders-svg.svg";
import CompletedOrdersSvg from "../../assets/completed-orders-svg.svg";
import CancelledOrdersSvg from "../../assets/cancelled-orders-svg.svg";

import FilterIcon from "../../assets/icons/FilterIcon";
import PrintIcon from "../../assets/icons/PrintIcon";
import SearchIcon from "../../assets/search-icon.svg";
import EyeIcon from "../../assets/icons/EyeIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import XIcon from "../../assets/icons/XIcon";
import CalendarIcon from "../../assets/calender-icon.svg";
import CancelModal from "./components/CancelModal";

import { useAppSelector } from "../../store/hooks";
import { getOrderIcon } from "../../utils/themeConfig";

function Orders() {
  const navigate = useNavigate();
  const [, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [balanceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("this_month");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showDateFilterDropdown, setShowDateFilterDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setSelectedOrder] = useState(null);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [orderStats, setOrderStats] = useState({
    total_orders: 0,
    pending_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const { isPartsURL, isAccessoriesURL } = useAppSelector(
    (state) => state.domain
  );

  const [, setDropdownPosition] = useState({ top: 0, left: 0 });
  const filterDropdownRef = useRef(null);
  const dateFilterDropdownRef = useRef(null);
  const statusDropdownRefs = useRef({});
  const statusTriggerRefs = useRef({});

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getOrders({
        search: searchTerm,
        page: currentPage,
        page_size: itemsPerPage,
        status: statusFilter,
        date_filter: dateFilter,
      });

      const orders = response.results || [];
      setOrders(orders);
      setFilteredOrders(orders);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
      setOrders([]);
      setFilteredOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    searchTerm,
    currentPage,
    itemsPerPage,
    statusFilter,
    dateFilter,
    balanceFilter,
  ]);

  const fetchOrderStats = async () => {
    setStatsLoading(true);
    try {
      const stats = await getOrderStats();
      setOrderStats(stats);
    } catch (error) {
      console.error("Error fetching order stats:", error);
      toast.error("Failed to fetch order statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderStats();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        dateFilterDropdownRef.current &&
        !dateFilterDropdownRef.current.contains(event.target)
      ) {
        setShowDateFilterDropdown(false);
      }
      const clickedInsideStatusDropdown = Object.values(
        statusDropdownRefs.current
      ).some((ref) => ref && ref.contains(event.target));
      const clickedOnTrigger = Object.values(statusTriggerRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (
        !clickedInsideStatusDropdown &&
        !clickedOnTrigger &&
        openStatusDropdown !== null
      ) {
        setOpenStatusDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openStatusDropdown]);

  // Update dropdown position on scroll
  useEffect(() => {
    if (openStatusDropdown !== null) {
      const updatePosition = () => {
        const triggerEl = statusTriggerRefs.current[openStatusDropdown];
        if (triggerEl) {
          const rect = triggerEl.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
          });
        }
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [openStatusDropdown]);

  // Handle search with debounce
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const totalOrdersCount = orderStats.total_orders || 0;
  const pendingOrdersCount = orderStats.pending_orders || 0;
  const completedOrdersCount = orderStats.completed_orders || 0;
  const cancelledOrdersCount = orderStats.cancelled_orders || 0;

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  const currentOrders = filteredOrders;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const shouldShowPagination = totalCount > itemsPerPage && totalPages > 1;

  const SortableHeader = ({ field, children }) => (
    <th className="sortable-header" onClick={() => handleSort(field)}>
      {children}
      {sortField === field && (
        <span className="ms-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
      )}
    </th>
  );

  const formatOrderDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const dateObj =
        typeof dateString === "string" ? new Date(dateString) : dateString;
      if (isNaN(dateObj.getTime())) return dateString || "-";

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[dateObj.getMonth()];
      const day = dateObj.getDate();
      const year = dateObj.getFullYear();

      return `${month} ${day}, ${year}`;
    } catch (error) {
      return dateString || "-";
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return null;

    const statusLower = status.toLowerCase();
    let className = "orders-status-badge";
    let displayText = status;

    if (statusLower === "processing") {
      className += " orders-status-processing";
      displayText = "Processing";
    } else if (statusLower === "shipped") {
      className += " orders-status-shipped";
      displayText = "Shipped";
    } else if (statusLower === "cancelled") {
      className += " orders-status-cancelled";
      displayText = "Cancelled";
    } else if (
      statusLower === "under_repair" ||
      statusLower === "under repair"
    ) {
      className += " orders-status-under-repair";
      displayText = "Under Repair";
    } else if (
      statusLower === "not_repairable" ||
      statusLower === "not repairable"
    ) {
      className += " orders-status-not-repairable";
      displayText = "Not Repairable";
    } else if (statusLower === "in_process" || statusLower === "in process") {
      className += " orders-status-in-process";
      displayText = "In Process";
    } else if (statusLower === "completed") {
      className += " orders-status-completed";
      displayText = "Completed";
    } else if (
      statusLower === "successfully_returned" ||
      statusLower === "successfully returned"
    ) {
      className += " orders-status-successfully-returned";
      displayText = "Successfully Returned";
    } else if (statusLower === "refund") {
      className += " orders-status-refund";
      displayText = "Refund";
    } else {
      className += " orders-status-default";
    }

    return <span className={className}>{displayText}</span>;
  };

  const statusFilterOptions = [
    { value: "all", label: "All Delivery" },
    { value: "processing", label: "Processing" },
    { value: "shipped", label: "Shipped" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const dateFilterOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "this_month", label: "This Month" },
  ];

  const getStatusFilterLabel = () => {
    const option = statusFilterOptions.find(
      (opt) => opt.value === statusFilter
    );
    return option ? option.label : "All Delivery";
  };

  const handleStatusFilterSelect = (value) => {
    setStatusFilter(value);
    setShowFilterDropdown(false);
    setCurrentPage(1);
  };

  const getDateFilterLabel = () => {
    const option = dateFilterOptions.find((opt) => opt.value === dateFilter);
    return option ? option.label : "This Month";
  };

  const handleDateFilterSelect = (value) => {
    setDateFilter(value);
    setShowDateFilterDropdown(false);
    setCurrentPage(1);
  };

  const handleCancelClick = (orderItem) => {
    setOrderToCancel(orderItem);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async (orderItem) => {
    try {
      await updateOrderStatus(orderItem.id, "cancelled");
      toast.success("Order cancelled successfully");
      fetchOrders();
      fetchOrderStats();
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error("Failed to cancel order");
    } finally {
      setShowCancelModal(false);
      setOrderToCancel(null);
    }
  };

  const handleCancelModalClose = () => {
    setShowCancelModal(false);
    setOrderToCancel(null);
  };

  return (
    <>
      {/* Content Area */}
      <div className="card rounded-3 p-4 border-0 mb-1 orders-container">
        <div className="orders-layout">
          {/* Header */}
          <div className="orders-top">
            <div className="orders-header">
              <div>
                <h2 className="orders-title">Orders</h2>
                <p className="orders-subtitle">
                  View and manage all customer orders placed on the website
                </p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-2">
              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Total Orders
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(totalOrdersCount)
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img
                        src={
                          isPartsURL
                            ? getOrderIcon("PartsTotalOrdersSvg", isPartsURL, isAccessoriesURL)
                            : isAccessoriesURL
                            ? getOrderIcon("AccTotalOrdersSvg", isPartsURL, isAccessoriesURL)
                            : getOrderIcon("TotalOrdersSvg")
                        }
                        alt="Total Orders"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Pending Orders
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(pendingOrdersCount)
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={PendingOrdersSvg} alt="Completed Orders" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Completed Orders
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(completedOrdersCount)
                        )}
                      </h4>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={CompletedOrdersSvg} alt="Pending Orders" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Cancelled Orders
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(cancelledOrdersCount)
                        )}
                      </h4>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={CancelledOrdersSvg} alt="Cancelled Orders" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search + Date Filter + Status Filter */}
            <div className="search-filter-section mb-4">
              {/* Search Bar */}
              <div className="orders-search-container">
                <input
                  type="text"
                  className={
                    isPartsURL
                      ? "orders-search-input-parts"
                      : isAccessoriesURL
                      ? "orders-search-input-acc"
                      : "orders-search-input"
                  }
                  placeholder="Search by customer name, order ID, or phone number"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <img
                  src={SearchIcon}
                  alt="Search"
                  className="orders-search-icon"
                />
              </div>

              {/* Date Filter */}
              <div
                className="orders-filter-dropdown"
                ref={dateFilterDropdownRef}
              >
                <button
                  className={
                    isPartsURL
                      ? "orders-date-filter-btn-parts"
                      : isAccessoriesURL
                      ? "orders-date-filter-btn-acc"
                      : "orders-date-filter-btn"
                  }
                  onClick={() =>
                    setShowDateFilterDropdown(!showDateFilterDropdown)
                  }
                >
                  <img src={CalendarIcon} alt="Calendar" />
                  {getDateFilterLabel()}
                  {showDateFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="orders-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="orders-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`orders-filter-dropdown-content ${
                    showDateFilterDropdown ? "show" : ""
                  }`}
                >
                  {dateFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`orders-filter-dropdown-item ${
                        dateFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleDateFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="orders-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* All Delivery Filter */}
              <div className="orders-filter-dropdown" ref={filterDropdownRef}>
                <button
                  className={
                    isPartsURL
                      ? "orders-statuses-filter-btn-parts"
                      : isAccessoriesURL
                      ? "orders-statuses-filter-btn-acc"
                      : "orders-statuses-filter-btn"
                  }
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <FilterIcon size={16} />
                  {getStatusFilterLabel()}
                  {showFilterDropdown ? (
                    <ChevronUp
                      size={16}
                      className="orders-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  ) : (
                    <ChevronDown
                      size={16}
                      className="orders-filter-dropdown-arrow"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#424A53",
                      }}
                    />
                  )}
                </button>

                <div
                  className={`orders-filter-dropdown-content ${
                    showFilterDropdown ? "show" : ""
                  }`}
                >
                  {statusFilterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`orders-filter-dropdown-item ${
                        statusFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleStatusFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="orders-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Clear Button */}
              <div className="clear-filter-btn-container">
                <button
                  className={
                    isPartsURL
                      ? "clear-filter-btn-parts"
                      : isAccessoriesURL
                      ? "clear-filter-btn-acc"
                      : "clear-filter-btn"
                  }
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    setDateFilter("this_month");
                    setCurrentPage(1);
                  }}
                >
                  Clear Filter
                </button>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <div className="orders-table-wrapper">
            <div className="orders-table-responsive">
              <table className="orders-table">
                <thead>
                  <tr>
                    <SortableHeader field="date">DATE</SortableHeader>
                    <SortableHeader field="order_id">ORDER ID</SortableHeader>
                    <SortableHeader field="customer">CUSTOMER</SortableHeader>
                    <SortableHeader field="products_count">
                      PRODUCTS
                    </SortableHeader>
                    <SortableHeader field="total">TOTAL</SortableHeader>
                    <SortableHeader field="delivery_status">
                      DELIVERY STATUS
                    </SortableHeader>
                    <th style={{ textAlign: "center" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h3 style={{ marginTop: "2rem" }}>
                            Loading orders...
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : currentOrders.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <NoDataIcon />
                          <h3 style={{ marginTop: "2rem" }}>No orders found</h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentOrders.map((orderItem) => {
                      return (
                        <tr key={orderItem.id}>
                          <td className="date-cell">
                            {formatOrderDate(orderItem.created_at)}
                          </td>
                          <td>{orderItem.order_code || "-"}</td>
                          <td>{orderItem.customer_name || "-"}</td>
                          <td>
                            {orderItem.items_count
                              ? `${orderItem.items_count} Items`
                              : "-"}
                          </td>
                          <td className="price-cell">
                            {formatCurrency(orderItem.total_amount || 0)}
                          </td>
                          <td>{getStatusBadge(orderItem.status)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className={
                                  isPartsURL
                                    ? "action-btn-parts view"
                                    : isAccessoriesURL
                                    ? "action-btn-acc view"
                                    : "action-btn view"
                                }
                                onClick={() => {
                                  setSelectedOrder(orderItem);
                                  navigate(`/orders/${orderItem.id}`);
                                }}
                                title="View order"
                              >
                                <EyeIcon
                                  fill={
                                    isPartsURL
                                      ? "var(--primary-color-parts)"
                                      : isAccessoriesURL
                                      ? "var(--primary-color-acc)"
                                      : "var(--primary-color-mobile)"
                                  }
                                />
                              </button>
                              <button
                                className={
                                  isPartsURL
                                    ? "action-btn-parts view"
                                    : isAccessoriesURL
                                    ? "action-btn-acc view"
                                    : "action-btn view"
                                }
                                onClick={() => {
                                  setSelectedOrder(orderItem);
                                  navigate(`/orders/${orderItem.id}/print`);
                                }}
                                title="Print order"
                              >
                                <PrintIcon
                                  fill={
                                    isPartsURL
                                      ? "var(--primary-color-parts)"
                                      : isAccessoriesURL
                                      ? "var(--primary-color-acc)"
                                      : "var(--primary-color-mobile)"
                                  }
                                />
                              </button>
                              <button
                                className="action-btn delete"
                                onClick={() => handleCancelClick(orderItem)}
                                title="Cancel order"
                              >
                                <XIcon className="action-icon delete" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {shouldShowPagination && (
            <div className="orders-pagination-sticky">
              <div className="orders-pagination-container">
                <div className="orders-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="orders-pagination-buttons">
                  <button
                    className="previous"
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={
                      isPartsURL
                        ? "next-orders-pagination-btn-parts"
                        : isAccessoriesURL
                        ? "next-orders-pagination-btn-acc"
                        : "next-orders-pagination-btn"
                    }
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                    }}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Order Modal */}
      <CancelModal
        isOpen={showCancelModal}
        onClose={handleCancelModalClose}
        onConfirm={handleCancelConfirm}
        orderItem={orderToCancel}
      />
    </>
  );
}

export default Orders;
